/* Bronson featured collection: recommendations, recently viewed and swatch overflow.
   The carousel itself is handled by bronson-carousel.js (Swiper). */
if (!customElements.get('bronson-featured-collection')) {
  customElements.define(
    'bronson-featured-collection',
    class BronsonFeaturedCollection extends HTMLElement {
      connectedCallback() {
        if (this.dataset.recentSection) {
          if (!this.recentRequested) this.loadRecentlyViewed();
          return;
        }
        if (this.dataset.recoUrl && !this.recoRequested) this.loadRecommendations();
        this.setup();
      }

      /* recently viewed: handles from localStorage (newest first), each card rendered by this section on that product's URL */
      async loadRecentlyViewed() {
        this.recentRequested = true;
        const wrap = this.closest('.bfc-wrap');
        const track = this.querySelector('[data-track]');
        const show = (slides) => {
          track.replaceChildren(...slides);
          wrap?.classList.remove('bfc-wrap--loading');
          this.setup();
        };
        const hide = () => {
          if (window.Shopify?.designMode) {
            // theme editor preview: show the current product so the section stays editable
            const preview = this.querySelector('template[data-recent-card]')?.content.firstElementChild;
            if (preview) return show([preview.cloneNode(true)]);
          }
          const section = this.closest('.shopify-section') || wrap;
          if (section) section.hidden = true;
        };

        let handles = [];
        try {
          handles = JSON.parse(window.localStorage.getItem('bronson:recently-viewed') || '[]');
        } catch (err) {}
        const current = this.dataset.recentCurrent;
        handles = handles.filter((h) => typeof h === 'string' && h && h !== current).slice(0, Number(this.dataset.recentLimit) || 15);
        if (!track || !handles.length) return hide();

        const root = window.Shopify?.routes?.root || '/';
        const gone = [];
        const cards = await Promise.all(
          handles.map(async (handle) => {
            try {
              const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.dataset.recentSection)}`);
              if (res.status === 404) gone.push(handle);
              if (!res.ok) return null;
              const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
              const card = doc.querySelector('template[data-recent-card]')?.content.firstElementChild;
              return card ? document.importNode(card, true) : null;
            } catch (err) {
              return null;
            }
          })
        );
        if (gone.length) {
          try {
            const stored = JSON.parse(window.localStorage.getItem('bronson:recently-viewed') || '[]');
            window.localStorage.setItem('bronson:recently-viewed', JSON.stringify(stored.filter((h) => !gone.includes(h))));
          } catch (err) {}
        }
        const slides = cards.filter(Boolean);
        return slides.length ? show(slides) : hide();
      }

      /* product recommendations: fetch the section rendered by Shopify and swap in its cards */
      async loadRecommendations() {
        this.recoRequested = true;
        const wrap = this.closest('.bfc-wrap');
        try {
          const res = await fetch(this.dataset.recoUrl);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = doc.querySelector('bronson-featured-collection');
          if (!fresh || !fresh.querySelector('[data-slide]')) throw new Error('No recommendations');
          this.innerHTML = fresh.innerHTML;
          wrap?.classList.remove('bfc-wrap--loading');
          this.setup();
        } catch (err) {
          const section = this.closest('.shopify-section') || wrap;
          if (section) section.hidden = true;
        }
      }

      setup() {
        this.swatchObservers?.forEach((o) => o.disconnect());
        this.viewport = this.querySelector('[data-viewport]');
        this.track = this.querySelector('[data-track]');
        if (!this.viewport || !this.track) return;
        this.initSwatches();
        window.BronsonCarousel?.refresh(this.viewport);
      }

      disconnectedCallback() {
        this.swatchObservers?.forEach((o) => o.disconnect());
      }

      initSwatches() {
        this.swatchObservers = [];
        this.querySelectorAll('[data-swatches]').forEach((wrap) => {
          const list = wrap.querySelector('[data-swatch-list]');
          const prev = wrap.querySelector('[data-swatch-prev]');
          const next = wrap.querySelector('[data-swatch-next]');
          if (!list) return;

          const check = () => {
            // when colors overflow, size the strip to whole swatches between the arrows
            list.style.maxWidth = '';
            const first = list.querySelector('.bfc-card__swatch');
            if (first && list.scrollWidth > list.clientWidth + 1) {
              const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
              const size = first.offsetWidth;
              const navSpace = (prev?.offsetWidth || 16) + (next?.offsetWidth || 16) + 2 * (parseFloat(getComputedStyle(wrap).columnGap) || 0);
              const available = wrap.clientWidth - navSpace;
              const count = Math.max(1, Math.floor((available + gap) / (size + gap)));
              list.style.maxWidth = `${count * size + (count - 1) * gap + 2}px`;
            }
            const overflow = list.scrollWidth > list.clientWidth + 1;
            if (prev) {
              prev.hidden = !overflow;
              prev.disabled = list.scrollLeft <= 1;
            }
            if (next) {
              next.hidden = !overflow;
              next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
            }
          };
          list.addEventListener('scroll', check, { passive: true });
          prev?.addEventListener('click', () => list.scrollBy({ left: -48 }));
          next?.addEventListener('click', () => list.scrollBy({ left: 48 }));
          const observer = new ResizeObserver(check);
          observer.observe(list);
          this.swatchObservers.push(observer);
          check();

          // preview the swatch's image on the card
          const img = wrap.closest('[data-card]')?.querySelector('[data-primary-image]');
          if (!img) return;
          const original = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
          wrap.querySelectorAll('[data-image]').forEach((swatch) =>
            swatch.addEventListener('mouseenter', () => {
              img.removeAttribute('srcset');
              img.src = swatch.dataset.image;
            })
          );
          wrap.addEventListener('mouseleave', () => {
            img.src = original.src;
            if (original.srcset) img.setAttribute('srcset', original.srcset);
          });
        });
      }
    }
  );
}
